/**
 * src/pages/SLA.jsx
 * Panel de SLA: semáforo de sistema por cliente (frescura de datos, errores,
 * próximos runs). Capa 3 de observabilidad — ver SLA_CM_MONITOR.md.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Header } from '../components/layout/Header'
import { KpiCard, Card } from '../components/ui/Card'
import { Badge, RESULTADO_COLOR } from '../components/ui/Badge'
import { Table } from '../components/ui/Table'
import { useSlaResumen, useSlaInforme } from '../hooks/useSla'

const SEMAFORO = {
  verde:    { color: 'green',  label: 'OK' },
  amarillo: { color: 'yellow', label: 'ATENCIÓN' },
  rojo:     { color: 'red',    label: 'CRÍTICO' },
  gris:     { color: 'gray',   label: 'INACTIVO' },
}

function fmtHoras(h) {
  if (h == null) return '—'
  if (h < 1) return `hace ${Math.round(h * 60)} min`
  if (h < 48) return `hace ${Math.round(h)} h`
  return `hace ${Math.round(h / 24)} días`
}

function fmtFecha(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('es-CL', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  })
}

const COLUMNS = (navigate) => [
  {
    key: 'semaforo', label: 'Estado', width: '110px',
    render: (v, row) => {
      const s = SEMAFORO[v] || SEMAFORO.gris
      return (
        <span title={row.motivo}>
          <Badge color={s.color} dot pulse={row.corriendo}>
            {row.corriendo ? 'CORRIENDO' : s.label}
          </Badge>
        </span>
      )
    },
  },
  {
    key: 'nombre', label: 'Cliente',
    render: (v, row) => (
      <div>
        <div style={{ fontWeight: 500 }}>{v}</div>
        <div style={{ fontSize: '11px', color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)' }}>
          {row.cliente_id}
        </div>
      </div>
    ),
  },
  {
    key: 'horas_desde_ok', label: 'Datos frescos', mono: true, nowrap: true,
    render: (v) => fmtHoras(v),
  },
  {
    key: 'ultimo_resultado_precios', label: 'Último run', width: '100px',
    render: (v) => v
      ? <Badge color={RESULTADO_COLOR[v] || 'gray'}>{v}</Badge>
      : <span style={{ color: 'var(--color-text-3)' }}>—</span>,
  },
  {
    key: 'errores_7d', label: 'Errores 7d', align: 'center', mono: true, width: '90px',
    render: (v) => (
      <span style={{ color: v > 0 ? 'var(--color-error)' : 'var(--color-text-3)', fontWeight: v > 0 ? 600 : 400 }}>
        {v}
      </span>
    ),
  },
  { key: 'freq_precios', label: 'Frecuencia', mono: true, nowrap: true, width: '100px' },
  {
    key: 'proximo_run_precios', label: 'Próximo run', mono: true, nowrap: true,
    render: (v) => fmtFecha(v),
  },
]

// ── Informe mensual (Capa 6) ──────────────────────────────────────────────────

function mesActual() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

function mesVecino(mes, delta) {
  const [y, m] = mes.split('-').map(Number)
  const d = new Date(y, m - 1 + delta, 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

function fmtMes(mes) {
  const [y, m] = mes.split('-').map(Number)
  return new Date(y, m - 1, 1).toLocaleDateString('es-CL', { month: 'long', year: 'numeric' })
}

const INFORME_COLUMNS = [
  {
    key: 'nombre', label: 'Cliente',
    render: (v, row) => (
      <div>
        <div style={{ fontWeight: 500 }}>{v}</div>
        <div style={{ fontSize: '11px', color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)' }}>
          {row.cliente_id}
        </div>
      </div>
    ),
  },
  {
    key: 'pct_frescura', label: 'Datos frescos', mono: true, nowrap: true,
    render: (v, row) => (
      <span>
        {row.dias_con_datos}/{row.dias_evaluables} días
        <span style={{ color: 'var(--color-text-3)' }}> · {v}%</span>
      </span>
    ),
  },
  {
    key: 'cumple_sla', label: 'SLA', width: '110px',
    render: (v) => (
      <Badge color={v ? 'green' : 'red'} dot>{v ? 'CUMPLE' : 'INCUMPLE'}</Badge>
    ),
  },
  {
    key: 'pct_exito_runs', label: 'Runs OK', mono: true, nowrap: true,
    render: (v, row) => {
      const total = row.runs_ok + row.runs_error
      if (!total) return <span style={{ color: 'var(--color-text-3)' }}>sin runs</span>
      return <span>{row.runs_ok}/{total} <span style={{ color: 'var(--color-text-3)' }}>· {v}%</span></span>
    },
  },
  {
    key: 'incidencias', label: 'Incidencias', align: 'center', mono: true, width: '100px',
    render: (v) => (
      <span style={{ color: v > 0 ? 'var(--color-warn)' : 'var(--color-text-3)', fontWeight: v > 0 ? 600 : 400 }}>
        {v}
      </span>
    ),
  },
]

function BotonMes({ children, onClick, disabled }) {
  return (
    <button
      onClick={onClick} disabled={disabled}
      style={{
        border: '1px solid var(--color-border)', background: 'var(--color-surface)',
        borderRadius: '6px', padding: '3px 9px', cursor: disabled ? 'default' : 'pointer',
        color: disabled ? 'var(--color-text-3)' : 'var(--color-text-2)',
        fontSize: '12px', fontFamily: 'var(--font-mono)',
      }}
    >
      {children}
    </button>
  )
}

function descargarCsvInforme(mes, clientes) {
  const cab = ['cliente_id', 'nombre', 'tier', 'dias_con_datos', 'dias_evaluables',
               'pct_frescura', 'cumple_sla', 'runs_ok', 'runs_error', 'pct_exito_runs',
               'incidencias']
  const filas = clientes.map(c => cab.map(k => {
    const v = c[k]
    if (typeof v === 'boolean') return v ? 'SI' : 'NO'
    if (v == null) return ''
    return String(v).includes(';') ? `"${v}"` : v
  }).join(';'))
  // BOM para que Excel abra el UTF-8 con acentos correctos; separador ';' (Excel es-ES/es-CL)
  const csv = '﻿' + [cab.join(';'), ...filas].join('\r\n')
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
  const a = document.createElement('a')
  a.href = url
  a.download = `informe_sla_${mes}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

function InformeMensual() {
  const navigate = useNavigate()
  const [mes, setMes] = useState(mesActual())
  const { data, isLoading } = useSlaInforme(mes)

  return (
    <Card
      title="Informe mensual"
      subtitle="Clic en un cliente para su informe individual (PDF)"
      padding="0"
      style={{ marginTop: '24px' }}
      action={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <BotonMes onClick={() => setMes(mesVecino(mes, -1))}>←</BotonMes>
          <span style={{
            fontSize: '12px', fontFamily: 'var(--font-mono)', color: 'var(--color-text)',
            minWidth: '120px', textAlign: 'center', textTransform: 'capitalize',
          }}>
            {fmtMes(mes)}
          </span>
          <BotonMes onClick={() => setMes(mesVecino(mes, 1))} disabled={mes >= mesActual()}>→</BotonMes>
          <span style={{ width: '8px' }} />
          <BotonMes onClick={() => descargarCsvInforme(mes, data?.clientes || [])}
                    disabled={isLoading || !data?.clientes?.length}>
            ⬇ CSV
          </BotonMes>
          <BotonMes onClick={() => navigate(`/sla/informe/print?mes=${mes}`)}
                    disabled={isLoading}>
            ⬇ PDF
          </BotonMes>
        </div>
      }
    >
      <Table
        columns={INFORME_COLUMNS}
        data={data?.clientes}
        loading={isLoading}
        emptyMessage="Sin datos para este mes"
        onRowClick={row => navigate(`/sla/informe/print?mes=${mes}&cliente=${row.cliente_id}`)}
      />
    </Card>
  )
}

export default function SLA() {
  const navigate = useNavigate()
  const { data, isLoading } = useSlaResumen()

  const resumen = data?.resumen || {}
  const umbrales = data?.umbrales || {}

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Header
        title="SLA"
        subtitle={`Estado de servicio por cliente · umbral de frescura ${umbrales.fresco_h ?? 26} h`}
      />

      <div style={{ flex: 1, overflow: 'auto', padding: '24px 28px' }}>
        {/* Agregados */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '14px',
          marginBottom: '24px',
        }}>
          <KpiCard
            label="EN SLA"
            value={isLoading ? '—' : (resumen.verde ?? 0)}
            color="var(--color-ok)"
            sub={`de ${resumen.total ?? 0} clientes activos`}
          />
          <KpiCard
            label="ATENCIÓN"
            value={isLoading ? '—' : (resumen.amarillo ?? 0)}
            color={resumen.amarillo > 0 ? 'var(--color-warn)' : 'var(--color-text-3)'}
            sub="frescura al límite o run fallido"
          />
          <KpiCard
            label="CRÍTICO"
            value={isLoading ? '—' : (resumen.rojo ?? 0)}
            color={resumen.rojo > 0 ? 'var(--color-error)' : 'var(--color-text-3)'}
            sub={`sin datos frescos ≥ ${umbrales.critico_h ?? 48} h`}
          />
          <KpiCard
            label="INACTIVOS"
            value={isLoading ? '—' : (resumen.gris ?? 0)}
            color="var(--color-text-3)"
            sub="scheduler en pausa"
          />
        </div>

        <Card
          title="Clientes"
          subtitle="Clic en un cliente para ver sus runs y logs"
          padding="0"
        >
          <Table
            columns={COLUMNS(navigate)}
            data={data?.clientes}
            loading={isLoading}
            emptyMessage="Sin clientes activos"
            onRowClick={(row) => navigate(`/scraping/${row.cliente_id}`)}
          />
        </Card>

        <InformeMensual />

        {data?.generado_en && (
          <div style={{
            marginTop: '12px', fontSize: '11px', color: 'var(--color-text-3)',
            fontFamily: 'var(--font-mono)',
          }}>
            Actualizado {fmtFecha(data.generado_en)} UTC · se refresca cada 60 s
          </div>
        )}
      </div>
    </div>
  )
}
